<template>
  <!-- Figma: Destinations+Themes と Preferences はそれぞれ単一ブロック内に並び、
       ブロック間にだけ 8px の透明ギャップが入る。compact 時は 0 件結果のフォールバック表示で、
       Destinations / Themes 見出し左に location_on / hashtag のアイコンを出す -->
  <div class="flex flex-col gap-2">
    <div v-if="destinations.length > 0 || themes.length > 0" class="flex flex-col">
      <section v-if="destinations.length > 0" class="flex flex-col gap-2 py-4">
        <div class="px-4">
          <SectionHeader title="Featured Destination" :icon-src="compact ? locationOnIcon : undefined" :on-view-more="props.onViewMoreDestinations" />
        </div>
        <div class="scrollbar-hide flex gap-2 overflow-x-auto px-4">
          <SearchInitialCard v-for="featured in destinations" :key="featured.id" :featured="featured" @click="props.onClose" />
        </div>
      </section>

      <section v-if="themes.length > 0" class="flex flex-col gap-2 py-4">
        <div class="px-4">
          <SectionHeader title="Explore by Theme" :icon-src="compact ? hashtagIcon : undefined" :on-view-more="props.onViewMoreThemes" />
        </div>
        <div class="scrollbar-hide flex gap-2 overflow-x-auto px-4">
          <SearchInitialCard v-for="featured in themes" :key="featured.id" :featured="featured" @click="props.onClose" />
        </div>
      </section>
    </div>

    <div v-if="props.onOpenDatesFilter || preferences.length > 0" class="flex flex-col">
      <section v-if="props.onOpenDatesFilter" class="flex flex-col gap-2 p-4">
        <SectionHeader title="Dates" view-more-label="Select Dates" :on-view-more="openDatesFilterRoot" />
        <div class="flex gap-1.5">
          <!-- Next 3 days: 直近 3 日間で Available なイベントがあるプランを検索結果ページで表示する -->
          <NuxtLink :class="chipClass" :to="next3DaysLink" @click="props.onClose"> Next 3 days </NuxtLink>
          <!-- Next 7 days: 直近 7 日間で Available なイベントがあるプランを検索結果ページで表示する -->
          <NuxtLink :class="chipClass" :to="next7DaysLink" @click="props.onClose"> Next 7 days </NuxtLink>
        </div>
      </section>

      <section v-if="preferences.length > 0" class="flex flex-col gap-2 p-4">
        <SectionHeader title="Preferences" :on-view-more="props.onViewMorePreferences" />
        <div class="flex flex-wrap gap-1.5">
          <NuxtLink v-for="tag in preferences" :key="tag.webPath" :class="chipClass" :to="buildPreferenceLink(tag.webPath)" @click="props.onClose">
            {{ tag.name }}
          </NuxtLink>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import hashtagIcon from '@/assets/icon/hashtag/gray.svg';
import locationOnIcon from '@/assets/icon/wabunka/location_on.svg';
import { generateSearchPath } from '@/components/organisms/SearchExperiences/generateSearchPath';
import type { WabunkaTagViewModel } from '@/models/wabunka/wabunkaTag';
import { formatHyphenatedDate, getCurrentJstDayjs } from '@/utils/date';
import type { MinimumFeaturedViewModel } from '@/wabunka-src/common/features/Featured/models/minimunFeatured/minimumFeatured';
import SearchInitialCard from '@/wabunka-src/features/Search/components/SearchInitialState/SearchInitialCard.vue';
import SectionHeader from '@/wabunka-src/features/Search/components/SearchInitialState/SectionHeader.vue';

interface Props {
  destinations: MinimumFeaturedViewModel[];
  themes: MinimumFeaturedViewModel[];
  preferences: WabunkaTagViewModel[];
  onClose: () => void;
  // 各セクション右上の View More: SearchExperiences フィルターモーダルを対応タブで開く
  onViewMoreDestinations?: () => void;
  onViewMoreThemes?: () => void;
  onViewMorePreferences?: () => void;
  // Dates セクション。指定時のみ表示し、Select Dates で日程フィルターモーダルを開く
  onOpenDatesFilter?: () => void;
  // 0 件結果時のフォールバック表示モード: Destinations / Themes 見出しにアイコンを付ける
  compact?: boolean;
}

const props = defineProps<Props>();

// Dates / Preferences で共通利用するチップ（ピル）状リンクのスタイル
const chipClass =
  'flex h-10 items-center justify-center whitespace-nowrap rounded-[4px] border-[0.5px] border-beige3 bg-white px-4 font-Spectral text-[16px] leading-6 text-gray4 transition-colors hover:border-gold3 hover:text-gold3';

// Preference タグの遷移先は Filter モーダル (SearchExperiences) と同じ generateSearchPath ロジックに揃える
const buildPreferenceLink = (webPath: string) => {
  const { path, query } = generateSearchPath(undefined, undefined, undefined, undefined, webPath);
  return { path, query };
};

// Dates の Select Dates: preset 指定なしで日程フィルターモーダルを開く
const openDatesFilterRoot = () => props.onOpenDatesFilter?.();

// 今日（JST）から指定日数分の日付を experienceEventStartAtDates に詰めて検索結果ページへ遷移するリンクを生成する
const buildNextDaysLink = (days: number) => {
  const today = getCurrentJstDayjs();
  const dates = Array.from({ length: days }, (_, i) => formatHyphenatedDate(today.add(i, 'day'))).join(',');
  const { path, query } = generateSearchPath(undefined, undefined, dates, undefined, undefined);
  return { path, query };
};

// Next 3 days / Next 7 days: 直近 N 日分の日付で検索結果ページへ遷移する
const next3DaysLink = computed(() => buildNextDaysLink(3));
const next7DaysLink = computed(() => buildNextDaysLink(7));
</script>

<style scoped>
.scrollbar-hide {
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
